import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Download, Trash2, Mail, Link2, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function NewsletterManager() {
  const [subscribers, setSubscribers] = useState([]);
  const [importUrl, setImportUrl] = useState("");
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const load = async () => { try { setSubscribers((await api.get("/newsletter/subscribers")).data); } catch {} };
  useEffect(() => { load(); }, []);

  const handleExport = async () => {
    try {
      const res = await api.get("/newsletter/export");
      const blob = new Blob([res.data.csv], { type: "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "newsletter_subscribers.csv"; a.click();
      toast.success(`${res.data.count} abonnés exportés`);
    } catch { toast.error("Erreur d'export"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ?")) return;
    try { await api.delete(`/newsletter/subscribers/${id}`); toast.success("Supprimé"); load(); } catch {}
  };

  const handleImport = async () => {
    if (!importUrl.trim()) return;
    setImporting(true);
    setImportResult(null);
    try {
      const res = await api.post("/newsletters/import", { url: importUrl.trim() });
      setImportResult({ ok: true, title: res.data.title, slug: res.data.slug, action: res.data.action });
      toast.success(`Newsletter "${res.data.title}" ${res.data.action === "created" ? "importée" : "mise à jour"} !`);
      setImportUrl("");
    } catch (e) {
      const msg = e.response?.data?.detail || "Erreur lors de l'import";
      setImportResult({ ok: false, error: msg });
      toast.error(msg);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div data-testid="newsletter-manager">
      <h1 className="font-heading text-3xl mb-8">Newsletter</h1>

      {/* HubSpot Import Section */}
      <div className="border rounded-lg p-6 mb-10 bg-muted/30" data-testid="hubspot-import-section">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Link2 className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h2 className="font-heading text-xl">Importer depuis HubSpot</h2>
            <p className="text-sm text-muted-foreground">Collez l'URL d'un numéro de La Toile pour l'importer automatiquement sur le blog.</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Input
            value={importUrl}
            onChange={(e) => setImportUrl(e.target.value)}
            placeholder="https://contact.ofalycee.org/la-toile-dofalycee-29"
            className="flex-1 rounded-none"
            disabled={importing}
            data-testid="hubspot-url-input"
          />
          <Button onClick={handleImport} disabled={importing || !importUrl.trim()} className="rounded-none px-6" data-testid="hubspot-import-btn">
            {importing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Link2 className="h-4 w-4 mr-2" />}
            {importing ? "Import en cours..." : "Importer"}
          </Button>
        </div>
        {importResult && (
          <div className={`mt-4 p-3 rounded-md flex items-start gap-2 text-sm ${importResult.ok ? "bg-green-50 text-green-800 border border-green-200" : "bg-red-50 text-red-800 border border-red-200"}`} data-testid="import-result">
            {importResult.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" /> : <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />}
            <div>
              {importResult.ok ? (
                <>
                  <strong>{importResult.title}</strong> — {importResult.action === "created" ? "importée avec succès" : "mise à jour"}.{" "}
                  <a href={`/article/${importResult.slug}`} className="underline" target="_blank" rel="noopener noreferrer">Voir l'article →</a>
                </>
              ) : importResult.error}
            </div>
          </div>
        )}
      </div>

      {/* Subscribers Section */}
      <div className="flex items-center justify-between mb-6">
        <div><h2 className="font-heading text-xl">Abonnés</h2><p className="text-muted-foreground mt-1">{subscribers.length} abonné(s)</p></div>
        <Button variant="outline" className="rounded-none" onClick={handleExport} data-testid="export-csv-btn"><Download className="h-4 w-4 mr-2" /> Exporter CSV</Button>
      </div>
      <div className="border rounded-none">
        <Table>
          <TableHeader><TableRow><TableHead>Email</TableHead><TableHead>Nom</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {subscribers.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center py-8 text-muted-foreground">Aucun abonné</TableCell></TableRow>
            : subscribers.map((sub) => (
              <TableRow key={sub.id}>
                <TableCell><div className="flex items-center gap-2"><Mail className="h-4 w-4 text-muted-foreground" />{sub.email}</div></TableCell>
                <TableCell>{sub.name || "\u2014"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{new Date(sub.subscribed_at).toLocaleDateString("fr-FR")}</TableCell>
                <TableCell className="text-right"><Button variant="ghost" size="icon" onClick={() => handleDelete(sub.id)} data-testid={`delete-sub-${sub.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
